import React, { useMemo, useState } from 'react';
import { Info, RefreshCw, SkipForward, X } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { MoneyInput } from '@components/MoneyInput';
import { Switch } from '@components/Switch';
import recurringService from '@services/recurringService';
import { DEFAULT_SCHEDULE_LEAD_DAYS, RECURRING_TARGET_TYPE_LABELS, type Account, type Category, type CreditCard, type RecurringTargetType, type RecurringTransaction, type RecurringTransactionInput } from '@/types/finance';
import { competenceLabel, currentCompetence, type Competence } from '@/utils/competence';
import { CategorySelect, CompetencePicker, MovementToggle, Required, ScheduleFields, labelCls } from './financeBits';

interface Props {
  recurring?: RecurringTransaction | null;
  /** Contas ativas. */
  accounts: Account[];
  /** Cartões ativos. */
  cards: CreditCard[];
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const signed = (movement: 'expense' | 'income', abs: number) => (movement === 'expense' ? -Math.abs(abs) : Math.abs(abs));

/**
 * Recorrência financeira (POST/PUT /recurring-transactions): alvo conta|cartão, dia, descrição,
 * Despesa/Receita + valor estimado, categoria, competência inicial e final (opcional). Em edição,
 * gerencia as competências puladas (POST/DELETE skips — efeito imediato). Fase 7: "Pago por
 * agendamento no banco" + dias de aviso (só conta e despesa).
 */
export const RecurringFormModal: React.FC<Props> = ({ recurring, accounts, cards, categories, onClose, onSaved, onError }) => {
  const isEdit = !!recurring;
  const [saving, setSaving] = useState(false);
  const [targetType, setTargetType] = useState<RecurringTargetType>(recurring?.target_type ?? 'account');
  const [targetId, setTargetId] = useState<number | ''>(recurring?.target_id ?? '');
  const [day, setDay] = useState(recurring?.transaction_day ?? 10);
  const [description, setDescription] = useState(recurring?.description ?? '');
  const [movement, setMovement] = useState<'expense' | 'income'>(() => (recurring && recurring.amount_cents > 0 ? 'income' : 'expense'));
  const [amount, setAmount] = useState<number | null>(recurring ? Math.abs(recurring.amount_cents) : null);
  const [categoryId, setCategoryId] = useState<number | ''>(recurring?.category_id ?? '');
  const [start, setStart] = useState<Competence | null>(recurring?.start_competence ?? currentCompetence());
  const [end, setEnd] = useState<Competence | null>(recurring?.end_competence ?? null);
  const [notes, setNotes] = useState(recurring?.notes ?? '');
  const [isActive, setIsActive] = useState(recurring?.is_active ?? true);
  const [requiresScheduling, setRequiresScheduling] = useState(recurring?.requires_scheduling ?? false);
  const [leadDays, setLeadDays] = useState(recurring?.schedule_lead_days ?? DEFAULT_SCHEDULE_LEAD_DAYS);
  /** Agendamento no banco só vale para despesa em conta (o backend devolve 400 nos outros casos). */
  const schedulingAllowed = targetType === 'account' && movement === 'expense';

  // Competências puladas (só em edição; chamadas imediatas)
  const [skips, setSkips] = useState<number[]>(recurring?.skips ?? []);
  const [skipAt, setSkipAt] = useState<Competence | null>(null);
  const [skipBusy, setSkipBusy] = useState(false);

  // Em edição o alvo pode estar inativo (fora da lista): mantém como opção.
  const targetOptions = useMemo(() => {
    const list: { id: number; name: string }[] = targetType === 'account' ? accounts : cards;
    if (recurring && recurring.target_type === targetType && !list.some((t) => t.id === recurring.target_id)) {
      return [...list, { id: recurring.target_id, name: `${recurring.target_name} (inativo)` }];
    }
    return list;
  }, [targetType, accounts, cards, recurring]);

  // Cartão só recebe despesas (compras) e estornos; mantém o toggle, mas a categoria é de despesa.
  const canSubmit = !saving && targetId !== '' && !!description.trim() && !!amount && start !== null && (end === null || end >= start) && day >= 1 && day <= 31;

  const save = async () => {
    if (!canSubmit || start === null) return;
    const payload: RecurringTransactionInput = {
      target_type: targetType,
      target_id: Number(targetId),
      transaction_day: day,
      description: description.trim(),
      amount_cents: signed(movement, amount as number),
      category_id: categoryId === '' ? null : categoryId,
      notes: notes.trim() || null,
      start_competence: start,
      end_competence: end,
      is_active: isActive,
      requires_scheduling: schedulingAllowed && requiresScheduling,
      schedule_lead_days: leadDays,
    };
    setSaving(true);
    try {
      if (recurring) await recurringService.update(recurring.id, payload);
      else await recurringService.create(payload);
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const addSkip = async () => {
    if (!recurring || skipAt === null) return;
    setSkipBusy(true);
    try {
      const r = await recurringService.addSkip(recurring.id, skipAt);
      setSkips(r.skips);
      setSkipAt(null);
    } catch (err) {
      onError(err);
    } finally {
      setSkipBusy(false);
    }
  };

  const removeSkip = async (c: number) => {
    if (!recurring) return;
    setSkipBusy(true);
    try {
      await recurringService.removeSkip(recurring.id, c);
      setSkips((s) => s.filter((x) => x !== c));
    } catch (err) {
      onError(err);
    } finally {
      setSkipBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <RefreshCw size={18} className="text-slate-500" />
        <h2 className="text-h3">{isEdit ? 'Editar recorrência' : 'Nova recorrência'}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-4">
        <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800 flex gap-2">
          <Info size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            O valor é uma <strong>estimativa</strong>: o sistema gera um lançamento por mês (12 meses à frente) e o atualiza quando você muda a recorrência.
            Ao editar um lançamento gerado, ele deixa de ser atualizado pela recorrência (marcar como <em>agendada</em> não conta como edição).
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>
              Onde lançar
              <Required />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 sm:items-center">
              <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden" role="radiogroup">
                {(['account', 'card'] as RecurringTargetType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={targetType === t}
                    onClick={() => {
                      setTargetType(t);
                      setTargetId('');
                    }}
                    className={targetType === t ? 'px-4 py-2 text-sm font-medium bg-emerald-600 text-white' : 'px-4 py-2 text-sm font-medium bg-white text-slate-600 hover:bg-slate-50'}
                  >
                    {RECURRING_TARGET_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
              <select className="select-field" value={targetId} onChange={(e) => setTargetId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Selecione…</option>
                {targetOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            {targetType === 'card' && <p className="text-small mt-1">No cartão, o item cai na fatura da competência da data gerada (pelo dia de fechamento).</p>}
          </div>
          <div className="sm:col-span-2">
            <MovementToggle value={movement} onChange={setMovement} labels={targetType === 'card' ? { expense: 'Compra', income: 'Estorno' } : undefined} />
          </div>
          <div>
            <label className={labelCls}>
              Dia do mês
              <Required />
            </label>
            <select className="select-field" value={day} onChange={(e) => setDay(Number(e.target.value))}>
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <p className="text-small mt-1">Dia 31 cai no último dia dos meses mais curtos.</p>
          </div>
          <div>
            <label className={labelCls}>
              Valor estimado
              <Required />
            </label>
            <MoneyInput value={amount} onChange={setAmount} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>
              Descrição
              <Required />
            </label>
            <input className="input-field" maxLength={200} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex.: Aluguel" />
          </div>
          <div>
            <label className={labelCls}>Categoria</label>
            <CategorySelect categories={categories} movement={targetType === 'card' ? 'expense' : movement} value={categoryId} onChange={setCategoryId} />
          </div>
          <div>
            <label className={labelCls}>Observações</label>
            <input className="input-field" maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>
              Primeira competência
              <Required />
            </label>
            <CompetencePicker value={start} onChange={setStart} />
          </div>
          <div>
            <label className={labelCls}>Última competência</label>
            <CompetencePicker value={end} onChange={setEnd} allowEmpty emptyLabel="Sem fim" />
            {end !== null && start !== null && end < start && <p className="text-xs text-red-600 mt-1">A última competência precisa ser igual ou posterior à primeira.</p>}
          </div>
          {schedulingAllowed && (
            <div className="sm:col-span-2">
              <ScheduleFields
                checked={requiresScheduling}
                onCheckedChange={setRequiresScheduling}
                leadDays={leadDays}
                onLeadDaysChange={setLeadDays}
                label="Pago por agendamento no banco"
                hint="Ex.: escola, plano de saúde. Cada mês gerado aparece como A agendar a partir desses dias antes do vencimento; o painel Hoje, o sino e um e-mail avisam enquanto não for marcado como agendado."
              />
            </div>
          )}
          {isEdit && (
            <div className="sm:col-span-2">
              <Switch checked={isActive} onChange={setIsActive} label="Recorrência ativa" />
              <p className="text-small mt-1">Inativar exclui os lançamentos futuros ainda não editados; os passados ficam.</p>
            </div>
          )}
        </div>

        {isEdit && recurring && (
          <section className="space-y-2 border-t border-slate-200 pt-3">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <SkipForward size={15} className="text-slate-500" /> Competências puladas
            </h3>
            <p className="text-small">Meses em que esta recorrência não gera lançamento (ex.: férias de uma mensalidade). As alterações aqui valem na hora.</p>
            {skips.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {[...skips].sort((a, b) => a - b).map((c) => (
                  <span key={c} className="badge-warning gap-1">
                    {competenceLabel(c)}
                    <button type="button" className="hover:text-red-700" title="Reativar (voltar a gerar)" disabled={skipBusy} onClick={() => removeSkip(c)}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:items-end">
              <CompetencePicker value={skipAt} onChange={setSkipAt} allowEmpty emptyLabel="Escolha o mês…" disabled={skipBusy} />
              <Button variant="outline" size="sm" icon={<SkipForward />} onClick={addSkip} loading={skipBusy} disabled={skipAt === null || skips.includes(skipAt ?? -1)}>
                Pular competência
              </Button>
            </div>
          </section>
        )}
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : 'Criar recorrência'}
        </Button>
      </div>
    </Modal>
  );
};
